import React, { useState, useEffect, useRef } from 'react'
import styles from './CommandPalette.module.css'
import apiClient from '../utils/api'

const CommandPalette = ({ isOpen, onClose, apiToken, onSuccess }) => {
  const [command, setCommand] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showSuggestions, setShowSuggestions] = useState(true)
  const inputRef = useRef(null)
  const resultsRef = useRef(null)

  // Focus input when palette opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
      loadSuggestions()
    }
  }, [isOpen])

  // Load suggestions on mount
  const loadSuggestions = async () => {
    if (!apiToken) return

    try {
      apiClient.setAuthToken(apiToken)
      const response = await apiClient.getCommandSuggestions()
      
      if (response.suggestions) {
        setSuggestions(response.suggestions)
      }
    } catch (err) {
      console.warn('Failed to load command suggestions:', err)
      // Set fallback suggestions
      setSuggestions({
        quick_actions: [
          'start timer',
          'stop timer',
          'log 2 hours yesterday',
          'show this week',
          'create entry for today 9am to 5pm'
        ],
        llm_status: 'unavailable'
      })
    }
  }

  // Handle command input changes
  const handleInputChange = (e) => {
    const value = e.target.value
    setCommand(value)
    setError(null)
    
    // Reset results when input changes
    if (results) {
      setResults(null)
      setShowSuggestions(true)
    }
    
    // Hide suggestions when user starts typing
    if (value.trim().length > 0) {
      setShowSuggestions(false)
    } else {
      setShowSuggestions(true)
      setSelectedIndex(0)
    }
  }

  // Execute command
  const executeCommand = async (commandText = command) => {
    if (!commandText.trim()) return

    setLoading(true)
    setError(null)
    setShowSuggestions(false)

    try {
      apiClient.setAuthToken(apiToken)
      const response = await apiClient.processCommand(commandText.trim())

      setResults(response)
      
      // Handle successful actions that should close the palette
      if (response.success && response.result?.action) {
        const action = response.result.action
        if (['timer_started', 'timer_stopped', 'worklog_created', 'navigate'].includes(action)) {
          // Trigger success callback to refresh main UI
          if (onSuccess) {
            onSuccess(action, response.result)
          }
          
          // Show success message briefly then close
          setTimeout(() => {
            handleClose()
          }, 2000)
        }
      }
    } catch (err) {
      console.error('Command execution failed:', err)
      console.error('Full error object:', err)
      console.error('Error stack:', err.stack)
      setError(err.message || 'Failed to execute command')
    } finally {
      setLoading(false)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return

    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        handleClose()
        break
        
      case 'Enter':
        e.preventDefault()
        if (showSuggestions && suggestions.quick_actions && suggestions.quick_actions[selectedIndex] && !command.trim()) {
          // Execute selected suggestion only if input is empty
          const selectedSuggestion = suggestions.quick_actions[selectedIndex]
          setCommand(selectedSuggestion)
          executeCommand(selectedSuggestion)
        } else if (command.trim()) {
          // Execute typed command
          executeCommand()
        }
        break
        
      case 'ArrowDown':
        e.preventDefault()
        if (showSuggestions && suggestions.quick_actions) {
          setSelectedIndex(prev => 
            prev < suggestions.quick_actions.length - 1 ? prev + 1 : 0
          )
        }
        break
        
      case 'ArrowUp':
        e.preventDefault()
        if (showSuggestions && suggestions.quick_actions) {
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.quick_actions.length - 1
          )
        }
        break
        
      case 'Tab':
        e.preventDefault()
        if (showSuggestions && suggestions.quick_actions && suggestions.quick_actions[selectedIndex]) {
          setCommand(suggestions.quick_actions[selectedIndex])
        }
        break
    }
  }

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setCommand(suggestion)
    executeCommand(suggestion)
  }

  // Close palette and reset state
  const handleClose = () => {
    setCommand('')
    setResults(null)
    setError(null)
    setSelectedIndex(0)
    setShowSuggestions(true)
    onClose()
  }

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  // Render result message
  const renderResult = () => {
    if (!results) return null

    const { success, result, error: resultError } = results

    if (!success) {
      return (
        <div className={styles.resultError}>
          <div className={styles.resultIcon}>❌</div>
          <div className={styles.resultText}>
            <strong>Error:</strong> {resultError || 'Command failed'}
          </div>
        </div>
      )
    }

    const { action, message, worklog } = result

    let icon = '✅'
    let title = 'Success'

    switch (action) {
      case 'timer_started':
        icon = '▶️'
        title = 'Timer Started'
        break
      case 'timer_stopped':
        icon = '⏹️'
        title = 'Timer Stopped'
        break
      case 'worklog_created':
        icon = '📝'
        title = 'Worklog Created'
        break
      case 'search_results':
        icon = '🔍'
        title = `Found ${result.worklogs?.length || 0} entries`
        break
      case 'navigate':
        icon = '🧭'
        title = 'Navigating...'
        break
      case 'unknown_command':
        icon = '❓'
        title = 'Unknown Command'
        break
      case 'requires_clarification':
        icon = '❓'
        title = 'Need More Info'
        break
    }

    return (
      <div className={styles.resultSuccess}>
        <div className={styles.resultIcon}>{icon}</div>
        <div className={styles.resultContent}>
          <div className={styles.resultTitle}>{title}</div>
          <div className={styles.resultMessage}>{message}</div>
          
          {worklog && (
            <div className={styles.resultDetails}>
              <div className={styles.worklogInfo}>
                <strong>{worklog.project_name || 'No project'}</strong>
                {worklog.description && (
                  <span className={styles.worklogDescription}>
                    - {worklog.description}
                  </span>
                )}
              </div>
            </div>
          )}
          
          {(result.suggestions || result.suggested_commands) && (
            <div className={styles.resultSuggestions}>
              <div className={styles.suggestionsTitle}>Try:</div>
              {(result.suggested_commands || result.suggestions).slice(0, 3).map((suggestion, index) => (
                <button
                  key={index}
                  className={styles.suggestionButton}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.palette}>
        <div className={styles.header}>
          <div className={styles.searchIcon}>🔍</div>
          <input
            ref={inputRef}
            type="text"
            value={command}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a command... (e.g., 'start timer', 'log 2 hours yesterday')"
            className={styles.input}
            disabled={loading}
          />
          {loading && <div className={styles.spinner}>⏳</div>}
        </div>

        <div className={styles.content}>
          {error && (
            <div className={styles.error}>
              <div className={styles.errorIcon}>⚠️</div>
              <div className={styles.errorText}>{error}</div>
            </div>
          )}

          {results ? (
            <div className={styles.results}>
              {renderResult()}
            </div>
          ) : showSuggestions && suggestions.quick_actions && (
            <div className={styles.suggestions}>
              <div className={styles.suggestionsHeader}>
                <span className={styles.suggestionsTitle}>Quick Actions</span>
                {suggestions.llm_status && (
                  <span className={`${styles.status} ${styles[suggestions.llm_status]}`}>
                    {suggestions.llm_status === 'available' ? '🤖 AI' : '📋 Basic'}
                  </span>
                )}
              </div>
              
              <div className={styles.suggestionsList}>
                {suggestions.quick_actions.map((suggestion, index) => (
                  <button
                    key={index}
                    className={`${styles.suggestionItem} ${
                      index === selectedIndex ? styles.selected : ''
                    }`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className={styles.suggestionText}>{suggestion}</div>
                    {index === selectedIndex && (
                      <div className={styles.suggestionHint}>Press Enter</div>
                    )}
                  </button>
                ))}
              </div>

              {suggestions.recent_projects && suggestions.recent_projects.length > 0 && (
                <div className={styles.suggestionsSection}>
                  <div className={styles.sectionTitle}>Recent Projects</div>
                  <div className={styles.chips}>
                    {suggestions.recent_projects.map((project, index) => (
                      <button
                        key={index}
                        className={styles.chip}
                        onClick={() => handleSuggestionClick(`start timer for ${project}`)}
                      >
                        {project}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {suggestions.templates && (
                <div className={styles.suggestionsSection}>
                  <div className={styles.sectionTitle}>Command Templates</div>
                  <div className={styles.templates}>
                    {suggestions.templates.map((template, index) => (
                      <div key={index} className={styles.template}>
                        {template}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.shortcuts}>
            <div className={styles.shortcut}>
              <kbd>↵</kbd> Execute
            </div>
            <div className={styles.shortcut}>
              <kbd>↑↓</kbd> Navigate
            </div>
            <div className={styles.shortcut}>
              <kbd>Tab</kbd> Autocomplete
            </div>
            <div className={styles.shortcut}>
              <kbd>Esc</kbd> Close
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CommandPalette